import { createFileRoute, useRouter } from '@tanstack/react-router'
import type { JSX } from 'react'

import { useLandingNavigation } from '../components/landing-navigation.tsx'
import { Button } from '../design-system/button.tsx'
import { Link } from '../design-system/link.tsx'
import { Panel } from '../design-system/panel.tsx'

export const Route = createFileRoute('/')({
  component: LandingPage
})

/** The employee's available sections, with their saved page choices applied. */
function LandingPage(): JSX.Element {
  const router = useRouter()
  const { sections, hiddenPages, openCustomization } = useLandingNavigation()
  const hidden = new Set(hiddenPages)

  return (
    <Panel
      data-testid="landing-page"
      title="Your workspace"
      description="Choose a section to open a page. You can change which pages appear in your navigation."
      width="wide"
    >
      <div>
        <Button
          data-testid="landing-customize"
          variant="outline"
          onClick={openCustomization}
        >
          Customize navigation
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {sections.map(section => {
          const pages = section.pages.filter(page => !hidden.has(page.id))
          const slug = section.group.toLowerCase().replace(/\s+/g, '-')

          return (
            <section
              key={section.group}
              data-testid={`landing-section-${slug}`}
              className="border-border bg-card rounded-lg border p-4"
            >
              <h2 className="text-lg font-semibold">{section.group}</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                {pages.length} {pages.length === 1 ? 'page' : 'pages'} shown
              </p>
              {pages.length === 0 ? (
                <p className="text-muted-foreground mt-4 text-sm">
                  All pages in this section are hidden. Customize navigation to
                  show one.
                </p>
              ) : (
                <ul className="mt-4 flex flex-col gap-2">
                  {pages.map(page => (
                    <li key={page.id}>
                      <Link
                        data-testid={`landing-link-${page.id}`}
                        href={page.path}
                        onNavigate={() => router.history.push(page.path)}
                        className="text-primary text-sm font-medium"
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )
        })}
      </div>
    </Panel>
  )
}
